import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@xing-com/button';
import { ProfileDocumentType } from '@xing-com/crate-common-graphql-types';
import type { ProfileDocument } from '@xing-com/crate-common-graphql-types';
import {
  DocumentUpload,
  DEFAULT_ACCEPTED_MIME_TYPES,
  DEFAULT_MAX_FILESIZE_BYTES,
  getSupportedFileTypes,
  isSupportedMimeType,
  UPLOAD_ERROR_COPY_KEYS,
  useAddFileUpload,
  useClearFileUpload,
  useFileUpload,
} from '@xing-com/crate-jobs-domain-apply-form-fields';
import { formatBytes } from '@xing-com/crate-jobs-domain-apply-helpers';
import { useBreakpoints } from '@xing-com/crate-jobs-hooks';
import type { NwtTrackingProps } from '@xing-com/crate-jobs-tracking';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import type {
  GetProfileDocumentsQuery,
  GetProfileDocumentsQueryVariables,
} from '@xing-com/crate-profile-graphql';
import { GetProfileDocumentsDocument } from '@xing-com/crate-profile-graphql';
import { ErrorState } from '@xing-com/empty-state';
import { useViewerData } from '@xing-com/hub';
import { ModalPage } from '@xing-com/modal-page';
import { PopUpWindow } from '@xing-com/pop-up-window';
import { Headline } from '@xing-com/typography';

import {
  MobileWrapper,
  OverflowWrapper,
  StyledSubline,
  StyledDivider,
  StyledButtonsWrapper,
  StyledUploadWrapper,
  StyledStatusBanner,
  StyledTextButton,
  StyledMultiselectCopy,
} from './document-hub-modal.styles';
import { DocumentItem } from './document-item';
import type { DocumentItemDocument } from './document-item';
import { DocumentLoadingSkeleton } from './document-loading-skeleton';

type Props = {
  show: boolean;
  testId?: string;
  documentType: ProfileDocumentType;
  headline: string;
  onCancel: () => void;
  onSelect: (
    document: SelectedDocument['document'],
    source: SelectedDocument['source'],
    stagedUploadId?: string
  ) => void;
  onSelectMultiple?: (documents: SelectedDocument['document'][]) => void;
  selectionMode?: 'single' | 'multiple';
  maxSelections?: number;
  initialSelectedDocumentIds?: string[];
  profileId?: string;
  allowViewerProfileFallback?: boolean;
  fieldId: string;
  maxSizeInBytes?: number;
  supportedTypes?: string;
  trackingProps: Omit<NwtTrackingProps, 'event' | 'eventSchema'>;
  origin: string;
};

type SelectedDocument =
  | {
      document: ProfileDocument;
      source: 'existing';
    }
  | {
      document: DocumentItemDocument;
      source: 'upload';
    };

type SelectedDocumentError =
  | 'FILE_SIZE_TOO_LARGE'
  | 'MIME_TYPE_NOT_SUPPORTED'
  | null;

export const getSelectedDocumentError = (
  document: Pick<ProfileDocument, 'format' | 'size'>,
  maxSizeInBytes = DEFAULT_MAX_FILESIZE_BYTES,
  supportedTypes = DEFAULT_ACCEPTED_MIME_TYPES
): SelectedDocumentError => {
  if (document.size > maxSizeInBytes) {
    return 'FILE_SIZE_TOO_LARGE';
  }

  return isSupportedMimeType(document.format, supportedTypes)
    ? null
    : 'MIME_TYPE_NOT_SUPPORTED';
};

export const DocumentHubModal: React.FC<Props> = ({
  show,
  onCancel,
  onSelect,
  onSelectMultiple,
  selectionMode = 'single',
  maxSelections = 1,
  initialSelectedDocumentIds = [],
  profileId,
  allowViewerProfileFallback = true,
  testId,
  documentType,
  headline,
  fieldId,
  maxSizeInBytes,
  supportedTypes,
  trackingProps,
  origin: originalOrigin,
}): React.ReactElement => {
  const intl = useIntl();
  const { isMediaSmallOrTiny } = useBreakpoints();
  const [documents, setDocuments] = useState<ProfileDocument[]>([]);
  const [selectedDocument, setSelectedDocument] =
    useState<SelectedDocument | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<
    SelectedDocument[]
  >([]);
  const [uploadedDocument, setUploadedDocument] =
    useState<SelectedDocument | null>(null);
  const [showSelectedDocumentError, setShowSelectedDocumentError] =
    useState(false);
  const hasInitializedSelection = useRef(false);
  const fileUploaded = useFileUpload(fieldId);
  const trackNwt = useJobsNwtTracking();
  const addFile = useAddFileUpload(fieldId);
  const clearFileUpload = useClearFileUpload(fieldId);
  const origin = originalOrigin.replace(/-/, '_');
  const handleCancel = useCallback(() => {
    if (
      fileUploaded?.status === 'loading-token' ||
      fileUploaded?.status === 'uploading'
    ) {
      fileUploaded.cancelUpload?.();
    } else if (fileUploaded?.status === 'success') {
      clearFileUpload();
    }
    setSelectedDocument(null);
    setSelectedDocuments([]);
    setUploadedDocument(null);
    setShowSelectedDocumentError(false);
    onCancel();
    trackNwt({
      eventSchema: 'basic',
      event: 'clicked',
      element: 'close',
      elementDetail: 'document_hub_upload_modal',
      ...trackingProps,
    });
  }, [clearFileUpload, fileUploaded, onCancel, trackNwt, trackingProps]);
  const handleUploadComplete = useCallback(
    (uploadedFile: {
      file: Pick<File, 'name' | 'size'>;
      fileServerId: string;
    }) => {
      setShowSelectedDocumentError(false);
      const uploadedDocument = {
        source: 'upload',
        document: {
          id: uploadedFile.fileServerId,
          title: uploadedFile.file.name,
          filename: uploadedFile.file.name,
          uploadDate: new Date().toISOString(),
          humanReadableSize: formatBytes(uploadedFile.file.size),
        },
      } as const;
      setSelectedDocument(uploadedDocument);
      setUploadedDocument(uploadedDocument);
      if (selectionMode === 'multiple') {
        setSelectedDocuments((documents) =>
          documents.some(
            (selected) => selected.document.id === uploadedDocument.document.id
          ) || documents.length >= maxSelections
            ? documents
            : [...documents, uploadedDocument]
        );
      }
    },
    [maxSelections, selectionMode]
  );
  const handleUploadDiscarded = useCallback(() => {
    setSelectedDocument((document) =>
      document?.source === 'upload' ? null : document
    );
    setUploadedDocument(null);
    setSelectedDocuments((documents) =>
      documents.filter((document) => document.source !== 'upload')
    );
    setShowSelectedDocumentError(false);
  }, []);
  const selectedDocumentError: SelectedDocumentError =
    selectedDocument?.source === 'existing'
      ? getSelectedDocumentError(
          selectedDocument.document,
          maxSizeInBytes,
          supportedTypes
        )
      : null;
  const multipleSelectedDocumentError = selectedDocuments
    .filter(
      (
        document
      ): document is Extract<SelectedDocument, { source: 'existing' }> =>
        document.source === 'existing'
    )
    .map((document) =>
      getSelectedDocumentError(
        document.document,
        maxSizeInBytes,
        supportedTypes
      )
    )
    .find(
      (error): error is Exclude<SelectedDocumentError, null> => error !== null
    );
  const documentSelectionError =
    selectionMode === 'multiple'
      ? multipleSelectedDocumentError
      : selectedDocumentError;
  const displayedDocuments = useMemo(
    () =>
      documents
        .filter((document) => document.documentType === documentType)
        .slice(0, 10),
    [documentType, documents]
  );
  const getTrackingPosition = useCallback(
    (document: SelectedDocument): number =>
      document.source === 'existing'
        ? displayedDocuments.findIndex(
            (displayedDocument) => displayedDocument.id === document.document.id
          )
        : 0,
    [displayedDocuments]
  );
  const toggleSelectedDocument = (document: ProfileDocument): void => {
    setShowSelectedDocumentError(false);
    setSelectedDocuments((documents) => {
      const isSelected = documents.some(
        (selected) => selected.document.id === document.id
      );
      if (isSelected) {
        return documents.filter(
          (selected) => selected.document.id !== document.id
        );
      }
      return documents.length < maxSelections
        ? [...documents, { source: 'existing', document }]
        : documents;
    });
  };
  const viewerData = useViewerData();
  const xingIdId =
    profileId ??
    (allowViewerProfileFallback ? viewerData?.data?.user?.id : undefined);
  const {
    error: queryError,
    loading: queryLoading,
    refetch,
  } = useQuery<GetProfileDocumentsQuery, GetProfileDocumentsQueryVariables>(
    GetProfileDocumentsDocument,
    {
      variables: {
        profileId: xingIdId || '',
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const queryFiles = data.profileDocuments?.collection || [];

        setDocuments(queryFiles);
        if (selectionMode === 'multiple' && !hasInitializedSelection.current) {
          hasInitializedSelection.current = true;
          setSelectedDocuments((selectedDocuments) => [
            ...queryFiles
              .filter((document) =>
                initialSelectedDocumentIds.includes(document.id)
              )
              .map((document) => ({ source: 'existing' as const, document })),
            ...selectedDocuments.filter(
              (document) => document.source === 'upload'
            ),
          ]);
        }
      },
      skip: !xingIdId || !show,
    }
  );
  const handleConfirm = useCallback((): void => {
    if (selectionMode === 'multiple') {
      if (documentSelectionError) {
        setShowSelectedDocumentError(true);
        return;
      }

      if (fileUploaded?.status === 'success') {
        clearFileUpload();
      }
      onSelectMultiple?.(
        selectedDocuments.map((selected) => selected.document)
      );

      const flags: string[] = [];

      selectedDocuments.forEach((document) => {
        const attribute = document.source === 'existing' ? 'reused' : 'new';
        const position = getTrackingPosition(document);

        flags.push(
          `${attribute}_${origin}${document.source === 'existing' ? `_position_${position}` : ''}`
        );
      });

      trackNwt({
        eventSchema: 'basic',
        event: 'clicked',
        element: 'add_document',
        elementDetail: 'document_hub_upload_modal',
        flags: flags.join(', '),
        ...trackingProps,
      });

      return;
    }

    if (!selectedDocument) {
      return;
    }

    if (selectedDocumentError) {
      setShowSelectedDocumentError(true);
      return;
    }

    if (fileUploaded?.status === 'success') {
      clearFileUpload();
    }
    onSelect(
      selectedDocument.document,
      selectedDocument.source,
      selectedDocument.source === 'upload'
        ? (fileUploaded?.stagedUploadId ?? undefined)
        : undefined
    );

    const attribute = selectedDocument.source === 'existing' ? 'reused' : 'new';
    const position = getTrackingPosition(selectedDocument);
    const flags = `${attribute}_${origin}${selectedDocument.source === 'existing' ? `_position_${position}` : ''}`;

    trackNwt({
      eventSchema: 'basic',
      event: 'clicked',
      element: 'add_document',
      elementDetail: 'document_hub_upload_modal',
      flags,
      ...trackingProps,
    });
  }, [
    clearFileUpload,
    documentSelectionError,
    onSelect,
    onSelectMultiple,
    selectedDocument,
    selectedDocumentError,
    selectedDocuments,
    selectionMode,
    trackNwt,
    trackingProps,
    origin,
    getTrackingPosition,
    fileUploaded?.stagedUploadId,
    fileUploaded?.status,
  ]);

  useEffect(() => {
    trackNwt({
      eventSchema: 'basic',
      event: 'viewed',
      element: `${origin}_modal`,
      elementDetail: 'document_hub_upload_modal',
      ...trackingProps,
    });
  }, [trackNwt, trackingProps, origin]);

  const modalContent = (
    <>
      <OverflowWrapper>
        <Headline size="xxxlarge" noMargin>
          <FormattedMessage id={headline} />
        </Headline>
        <StyledSubline>
          <FormattedMessage id="JOBS_DOCUMENT_HUB_MODAL_SUBLINE" />
        </StyledSubline>
        <StyledUploadWrapper>
          {uploadedDocument && selectedDocument ? (
            <DocumentItem
              document={uploadedDocument.document}
              selectedDocument={selectedDocument}
              selectionMode={selectionMode}
              isSelected={selectedDocuments.some(
                (selected) =>
                  selected.document.id === uploadedDocument.document.id
              )}
              onChange={() => {
                setShowSelectedDocumentError(false);
                if (selectionMode === 'multiple') {
                  setSelectedDocuments((documents) =>
                    documents.some(
                      (selected) =>
                        selected.document.id === uploadedDocument.document.id
                    )
                      ? documents.filter(
                          (selected) =>
                            selected.document.id !==
                            uploadedDocument.document.id
                        )
                      : documents.length < maxSelections
                        ? [...documents, uploadedDocument]
                        : documents
                  );
                } else {
                  setSelectedDocument(uploadedDocument);
                }
              }}
            />
          ) : (
            <></>
          )}
          <DocumentUpload
            origin={origin}
            onUploadComplete={handleUploadComplete}
            onUploadDiscarded={handleUploadDiscarded}
            maxSizeInBytes={maxSizeInBytes}
            supportedTypes={supportedTypes}
            fileUploaded={fileUploaded}
            uploadFile={addFile}
            nwtProps={trackingProps}
          />
        </StyledUploadWrapper>

        {displayedDocuments.length > 0 && !queryLoading ? (
          <>
            <StyledDivider />
            {documentType === ProfileDocumentType.Cv ? (
              <Headline size="xlarge">
                <FormattedMessage id="PROFILE_DOCUMENTS_YOUR_CVS_SECTION" />
              </Headline>
            ) : (
              <Headline size="xlarge">
                <FormattedMessage id="PROFILE_DOCUMENTS_OTHER_DOCUMENTS_SECTION" />
              </Headline>
            )}
            {selectionMode === 'multiple' ? (
              <StyledMultiselectCopy>
                <FormattedMessage id="PROFILE_DOCUMENTS_MULTISELECT_INFO" />
              </StyledMultiselectCopy>
            ) : null}
          </>
        ) : (
          <>{queryLoading ? <StyledDivider /> : null}</>
        )}

        <StyledStatusBanner
          aria-live="polite"
          display="inline"
          variant="error"
          show={showSelectedDocumentError && !!documentSelectionError}
        >
          {documentSelectionError ? (
            <FormattedMessage
              id={UPLOAD_ERROR_COPY_KEYS[documentSelectionError]}
              values={{
                maxSize: formatBytes(
                  maxSizeInBytes ?? DEFAULT_MAX_FILESIZE_BYTES
                ),
                fileTypes: getSupportedFileTypes(
                  supportedTypes ?? DEFAULT_ACCEPTED_MIME_TYPES
                ),
              }}
            />
          ) : null}
        </StyledStatusBanner>

        {queryError ? (
          <ErrorState
            headlineText={intl.formatMessage({
              id: 'JOBS_DOCUMENT_HUB_MODAL_ERROR_TITLE',
            })}
            bodyCopyText={intl.formatMessage({
              id: 'JOBS_DOCUMENT_HUB_MODAL_ERROR_TEXT',
            })}
            buttonText={intl.formatMessage({
              id: 'JOBS_DOCUMENT_HUB_MODAL_TRY_AGAIN',
            })}
            buttonProps={{
              onClick: () => {
                refetch();
              },
            }}
          />
        ) : queryLoading ? (
          <DocumentLoadingSkeleton />
        ) : (
          displayedDocuments.map((document) => (
            <DocumentItem
              key={document.id}
              document={document}
              selectedDocument={selectedDocument}
              selectionMode={selectionMode}
              isSelected={selectedDocuments.some(
                (selected) => selected.document.id === document.id
              )}
              onChange={() => {
                if (selectionMode === 'multiple') {
                  toggleSelectedDocument(document);
                } else {
                  setShowSelectedDocumentError(false);
                  setSelectedDocument({ source: 'existing', document });
                }

                trackNwt({
                  eventSchema: 'basic',
                  event: 'clicked',
                  element: `saved_${origin}_select`,
                  elementDetail: 'document_hub_upload_modal',
                  ...trackingProps,
                });
              }}
            />
          ))
        )}
      </OverflowWrapper>

      <StyledDivider />

      <StyledButtonsWrapper>
        <StyledTextButton onClick={handleCancel}>
          <FormattedMessage id="JOBS_DOCUMENT_HUB_MODAL_CANCEL" />
        </StyledTextButton>
        <Button
          variant="primary"
          disabled={
            selectionMode === 'multiple'
              ? selectedDocuments.length === 0
              : !selectedDocument
          }
          onClick={handleConfirm}
        >
          <FormattedMessage id="JOBS_DOCUMENT_HUB_MODAL_ADD_DOCUMENT" />
        </Button>
      </StyledButtonsWrapper>
    </>
  );

  return isMediaSmallOrTiny ? (
    <ModalPage
      closeButton={{ onClick: handleCancel }}
      isAnimated
      isShown={show}
      noGrid
      data-testid={testId}
    >
      <MobileWrapper>{modalContent}</MobileWrapper>
    </ModalPage>
  ) : (
    <PopUpWindow
      isShown={show}
      hide={handleCancel}
      onDimmerClick={handleCancel}
      data-testid={testId}
    >
      {modalContent}
    </PopUpWindow>
  );
};
